import PropTypes from "prop-types";
import { TrendingUp, TrendingDown, Loader2 } from "lucide-react";
import { formatValue } from "./utils";

function Sparkline({ data, color, width = 64, height = 22 }) {
  if (!data || data.length < 2) return null;
  const min = Math.min(...data);
  const max = Math.max(...data);
  const range = max - min || 1;
  const step = width / (data.length - 1);
  const points = data.map((v, i) => `${i * step},${height - ((v - min) / range) * (height - 2) - 1}`).join(" ");
  return (
    <svg width={width} height={height} className="kpi-card__sparkline" viewBox={`0 0 ${width} ${height}`}>
      <polyline
        fill="none"
        stroke={color}
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
        points={points}
      />
    </svg>
  );
}

Sparkline.propTypes = {
  data: PropTypes.arrayOf(PropTypes.number),
  color: PropTypes.string.isRequired,
  width: PropTypes.number,
  height: PropTypes.number,
};

export default function KpiCard({
  label,
  value,
  unit,
  icon: Icon,
  iconClass,
  trend,
  trendDir,
  prefix,
  sparkData,
  sparkColor,
  loading,
}) {
  const displayValue = typeof value === "number" ? formatValue(value, prefix) : (prefix || "") + (value ?? "\u2014");

  return (
    <div className="kpi-card">
      <div className={`kpi-card__icon ${iconClass}`}>
        <Icon size={22} />
      </div>
      <div className="kpi-card__content">
        <span className="kpi-card__label">{label}</span>
        <div className="kpi-card__value-row">
          {loading ? (
            <span className="kpi-card__value kpi-card__value--loading">
              <Loader2 size={16} className="kpi-card__spinner" />
            </span>
          ) : (
            <span className="kpi-card__value">
              {displayValue}
              {unit && <span className="kpi-card__unit">{unit}</span>}
            </span>
          )}
          {sparkData && (
            <Sparkline data={sparkData} color={sparkColor || (trendDir === "up" ? "#34d399" : "#ef4444")} />
          )}
        </div>
        {trend && trendDir && (
          <span className={`kpi-card__trend kpi-card__trend--${trendDir}`}>
            {trendDir === "up" ? <TrendingUp size={12} /> : <TrendingDown size={12} />}
            {trend} vs last week
          </span>
        )}
      </div>
    </div>
  );
}

KpiCard.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  unit: PropTypes.string,
  icon: PropTypes.elementType.isRequired,
  iconClass: PropTypes.string.isRequired,
  trend: PropTypes.string,
  trendDir: PropTypes.oneOf(["up", "down"]),
  prefix: PropTypes.string,
  sparkData: PropTypes.arrayOf(PropTypes.number),
  sparkColor: PropTypes.string,
  loading: PropTypes.bool,
};

KpiCard.defaultProps = {
  value: 0,
  unit: "",
  prefix: "",
  sparkData: null,
  sparkColor: null,
  trend: null,
  trendDir: null,
  loading: false,
};
